/**
 * The member's documents: the legacy Attachments screens, on this API.
 *
 * Two modals, as legacy had them. **View** is a row of cards — a thumbnail for
 * an image, a file glyph for anything else — each with preview, download and
 * delete. Deleting marks a card rather than removing it: the list is a working
 * copy until Save, so a mis-click is undone by closing, and several documents
 * go in one round trip. **Add** is a type and a file, and nothing else.
 *
 * Ported from `legacy/frontend/src/apps/mrm/components/MrmRowDetailView/
 * MrmAttachments`. What legacy needed and this does not: presigned URLs — a
 * document here is a data URL, so it is already in the browser's hands.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, FileText, Trash2 } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilePreview, canPreview, isImageData } from "@/components/file-preview";
import { Modal } from "@/components/ui/dialog";
import { Field, Form } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip } from "@/components/ui/tooltip";
import { api, errorMessage, ifMatch } from "@/services/api";
import { saveFile } from "@/utils/download";
import { atLeast, useCan, useRank } from "@/utils/permissions";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

export interface Attachment {
  id: number;
  title: string;
  file_url: string;
  /** What it was uploaded as; null on rows attached before that was kept. */
  file_name?: string | null;
  updated_at?: string;
}

/** The six the form offers, as the server spells them. */
export const ATTACHMENT_TITLES = [
  "application_form",
  "cancellation_form",
  "documents",
  "id_card",
  "membership_information_update_form",
  "others",
] as const;

/** What the picker offers, as legacy's uploader did. */
export const ATTACHMENT_ACCEPT =
  ".jpg,.jpeg,.png,.pdf,.csv,.xls,.xlsx,.doc,.docx,.txt," +
  "image/jpeg,image/png,application/pdf,text/csv,text/plain";

const MAX_MB = 10;
const ALL = "__all__";

/** `application_form` as a person writes it. */
export const titleCase = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

/**
 * What to call the document.
 *
 * The name it was uploaded under, where the row has one. There is nowhere else
 * to read it from: the key it is stored under is ours, and `file_url` here is a
 * signed URL with a query string on it. Rows attached before the name was kept
 * fall back to the old made-up one — title and media type — which is at least
 * what they have always shown.
 */
const downloadName = (row: Attachment) => {
  if (row.file_name) return row.file_name;
  const media = /^data:([\w.+-]+\/[\w.+-]+)/i.exec(row.file_url)?.[1] ?? "";
  // A stored file's key carries the extension the document went in with, so a
  // row with no name still says what kind of thing it is: `cancellation_form
  // .pdf` rather than `.bin`, which is what a signed URL used to come out as.
  const stored = /\.([a-z0-9]{1,8})(?:$|\?)/i.exec(row.file_url.split("/").pop() ?? "")?.[1];
  const extension =
    media.split("/")[1]?.replace("jpeg", "jpg").slice(0, 5) || stored?.toLowerCase() || "bin";
  return `${row.title}.${extension}`;
};

const listKey = (memberId: number) => [`/members/${memberId}/attachments`] as const;

export function useAttachments(memberId: number) {
  return useQuery({
    queryKey: listKey(memberId),
    queryFn: async ({ signal }) =>
      (await api.get<Attachment[]>(`/members/${memberId}/attachments`, { signal })).data,
  });
}

/**
 * The documents of one member, as cards.
 *
 * `marked` is what legacy called marked-for-deletion: the card leaves the list
 * at once, and Save is what actually removes it. Nothing is written until then.
 */
export function ViewAttachmentsModal({
  memberId,
  accountNumber,
  onClose,
}: {
  memberId: number;
  accountNumber?: string | undefined;
  onClose: () => void;
}) {
  const t = useTranslation();
  const allow = useCan();
  const rank = useRank();
  const queryClient = useQueryClient();
  const rows = useAttachments(memberId);
  const [marked, setMarked] = useState<ReadonlySet<number>>(new Set());
  const [type, setType] = useState(ALL);
  const [preview, setPreview] = useState<Attachment | null>(null);
  // Which row is being fetched, so its own icon spins and a second press of it
  // does nothing. By id rather than a boolean: two different documents may be
  // asked for at once, and neither should disable the other's button.
  const [downloading, setDownloading] = useState<ReadonlySet<number>>(new Set());

  const list = useMemo(() => rows.data ?? [], [rows.data]);
  // Only the types this member actually has, so the filter never offers an
  // answer of nothing.
  const types = useMemo(() => [...new Set(list.map((row) => row.title))].sort(), [list]);
  const shown = list.filter(
    (row) => !marked.has(row.id) && (type === ALL || row.title === type),
  );

  const remove = useMutation({
    mutationFn: async () => {
      // One at a time: each carries its own If-Match, and a row someone else
      // has already removed should not stop the others.
      for (const id of marked) {
        const row = list.find((one) => one.id === id);
        await api.delete(`/members/${memberId}/attachments/${id}`, ifMatch(row));
      }
    },
    onSuccess: async () => {
      toast.success(t("att.deleted"));
      await queryClient.invalidateQueries({ queryKey: listKey(memberId) });
      setMarked(new Set());
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t("att.deleteFailed"))),
  });

  /**
   * Save the document under the name it was uploaded as.
   *
   * Through the API, not by linking at `file_url`. That is a presigned S3 URL,
   * and a browser ignores the `download` attribute on a cross-origin link — so
   * the old link navigated to S3 and the file arrived named after the key it
   * is stored under, `34b15e8001234539a39bbb558ad6d6b8.jpg`. Fetching the URL
   * here to rename it is not open either: the bucket sends no
   * `Access-Control-Allow-Origin`, so the browser blocks the read before it
   * starts. The route hands back the bytes with the right name on them, and —
   * because it is something to wait on rather than a navigation — it is also
   * what lets this say whether the download actually worked.
   *
   * Saved through `saveFile`, so "File downloaded successfully." is said once
   * the file has been written rather than once it has been handed to the
   * browser — on a browser set to ask where to save, the message used to
   * appear over a dialog nobody had answered yet. A dismissed dialog says
   * nothing at all: nothing failed, and nothing was downloaded either.
   */
  const download = async (row: Attachment) => {
    if (downloading.has(row.id)) return;
    setDownloading((was) => new Set(was).add(row.id));
    try {
      const saved = await saveFile(downloadName(row), async () => {
        const answer = await api.get<Blob>(
          `/members/${memberId}/attachments/${row.id}/download`,
          { responseType: "blob" },
        );
        return answer.data;
      });
      if (saved === "saved") toast.success(t("att.downloaded"));
    } catch {
      // Deliberately not `errorMessage`: whatever went wrong upstream, what the
      // person can do about it is the same, and the brief is one sentence.
      toast.error(t("att.downloadFailed"));
    } finally {
      setDownloading((was) => {
        const next = new Set(was);
        next.delete(row.id);
        return next;
      });
    }
  };

  const toggle = (id: number) =>
    setMarked((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const canDelete = allow("delete__member_attachment");
  // Taking a copy of a member's documents off the premises is a step beyond
  // looking at one, so it stops at the desk: an Associate and a Supervisor may
  // open an attachment and read it, and a Manager up may save it. Not a
  // codename, because it is not a grant anybody is given one at a time — it is
  // where somebody sits.
  const mayDownload = atLeast(rank, "manager");
  const title = accountNumber
    ? `${t("md.attachments")} — ${accountNumber}`
    : t("md.attachments");

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={title}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.close")}
            </Button>
            <Button
              disabled={marked.size === 0}
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t("rp.save")}
            </Button>
          </>
        }
      >
        <div className="grid gap-4 pb-2">
          {types.length > 1 ? (
            <OptionSelect
              aria-label={t("att.filterByType")}
              value={type}
              onChange={setType}
              clearable={false}
              fieldSize="sm"
              className="w-64"
              choices={[
                { value: ALL, label: t("att.allTypes") },
                ...types.map((one) => ({ value: one, label: titleCase(one) })),
              ]}
            />
          ) : null}

          {rows.isPending ? (
            <div className="grid place-items-center py-10">
              <Spinner />
            </div>
          ) : shown.length === 0 ? (
            <p className="text-fg-muted text-callout py-10 text-center">
              {type === ALL
                ? t("att.none")
                : t("att.noneOfType").replace("{type}", titleCase(type))}
            </p>
          ) : (
            <div
              className={cn(
                "flex flex-wrap gap-3",
                // One document sits in the middle, several fill the row from
                // the left — legacy's `--centered`, which it puts on the list
                // only when there is a single file. A lone tile pinned to the
                // left of a sheet this wide reads as the first of several that
                // failed to load.
                shown.length === 1 && "justify-center",
              )}
            >
              {shown.map((row) => (
                <figure
                  key={row.id}
                  className="border-line bg-surface-sunken relative flex w-44 flex-col gap-2 rounded-xl border p-3"
                >
                  {type === ALL ? (
                    <figcaption className="text-fg-subtle text-footnote truncate font-medium">
                      {titleCase(row.title)}
                    </figcaption>
                  ) : null}
                  <div className="bg-surface-raised grid h-24 place-items-center overflow-hidden rounded-lg">
                    {isImageData(row.file_url) ? (
                      <img
                        src={row.file_url}
                        alt={titleCase(row.title)}
                        className="size-full object-cover"
                      />
                    ) : (
                      <FileText className="text-fg-subtle size-8" aria-hidden />
                    )}
                  </div>
                  <span className="text-fg text-footnote truncate">{downloadName(row)}</span>
                  <div className="flex items-center gap-1">
                    {/* Shown for anything the viewer can actually draw — a PDF
                        as well as a picture now. Preview and Download stay two
                        separate things: one opens it here, the other saves it. */}
                    {canPreview(row.file_url) ? (
                      <IconAction label={t("att.preview")} onClick={() => setPreview(row)}>
                        <Eye className="size-3.5" aria-hidden />
                      </IconAction>
                    ) : null}
                    {mayDownload ? (
                      <IconAction
                        label={t("hist.download")}
                        busy={downloading.has(row.id)}
                        onClick={() => void download(row)}
                      >
                        <Download className="size-3.5" aria-hidden />
                      </IconAction>
                    ) : null}
                    {canDelete ? (
                      <IconAction
                        label={t("common.delete")}
                        danger
                        onClick={() => toggle(row.id)}
                      >
                        <Trash2 className="size-3.5" aria-hidden />
                      </IconAction>
                    ) : null}
                  </div>
                </figure>
              ))}
            </div>
          )}

          {marked.size > 0 ? (
            <p className="text-fg-muted text-footnote">
              {t("att.markedForDeletion").replace("{count}", String(marked.size))}{" "}
              <button
                type="button"
                onClick={() => setMarked(new Set())}
                className="text-accent font-medium hover:underline"
              >
                {t("common.undo")}
              </button>
            </p>
          ) : null}
        </div>
      </Modal>

      {preview ? (
        <FilePreview
          src={preview.file_url}
          title={titleCase(preview.title)}
          // The viewer offers the same save, so it answers to the same rule and
          // goes down the same route — undefined draws no button at all.
          onDownload={mayDownload ? () => void download(preview) : undefined}
          downloading={downloading.has(preview.id)}
          onClose={() => setPreview(null)}
        />
      ) : null}
    </>
  );
}

function IconAction({
  label,
  danger = false,
  busy = false,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  /** Working: the icon becomes a spinner and the button stops answering. */
  busy?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    // The app's own tooltip rather than the browser's `title`, which the
    // others on this card already looked like they had: it appears at once
    // instead of after a second, and it is the same black box as everywhere
    // else. `title` stays off, or both would show.
    <Tooltip label={label}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        aria-busy={busy}
        className={cn(
          "text-fg-muted grid size-7 place-items-center rounded-lg transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-60",
          danger
            ? "hover:bg-danger-soft hover:text-danger"
            : "hover:bg-surface-inset hover:text-fg",
        )}
      >
        {busy ? <Spinner className="size-3.5" /> : children}
        <span className="sr-only">{label}</span>
      </button>
    </Tooltip>
  );
}

/**
 * Add Attachment: a type and a file.
 *
 * Save stays disabled until both are answered, as legacy's did — a form that
 * lets you press the button and then tells you off has wasted the press.
 */
export function AddAttachmentModal({
  memberId,
  onClose,
}: {
  memberId: number;
  onClose: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<{ url: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      // The name goes with it: storing the file gives it a key of our own
      // making, and the member's activity log should say what was uploaded.
      api.post(`/members/${memberId}/attachments`, {
        title,
        file_url: file?.url,
        file_name: file?.name,
      }),
    onSuccess: async () => {
      toast.success(t("att.added"));
      await queryClient.invalidateQueries({ queryKey: listKey(memberId) });
      // An ID card is also the member's own, so the page behind this is stale.
      await queryClient.invalidateQueries({ queryKey: ["/members", memberId] });
      onClose();
    },
    onError: (err) => setError(errorMessage(err, t("att.addFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!title || !file) return setError(t("att.typeAndFileRequired"));
    save.mutate();
  };

  const formId = "add-attachment";
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t("rp.addSingular").replace("{singular}", t("hist.attachment"))}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={!title || !file}
            loading={save.isPending}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <Form id={formId} className="grid gap-4 pb-2" onSubmit={submit}>
        <Field label={t("hist.title")} htmlFor="attachment-type" required>
          <OptionSelect
            id="attachment-type"
            value={title}
            onChange={setTitle}
            required
            placeholder={t("att.selectType")}
            choices={ATTACHMENT_TITLES.map((one) => ({ value: one, label: titleCase(one) }))}
          />
        </Field>

        {/* What was chosen above, said back as the list will show it. Legacy
            heads the panel with the type (`__type-header`), and it is the one
            thing on this form that cannot be read off the fields once the
            picker has collapsed. */}
        {title ? (
          <Badge tone="accent" dot className="w-fit">
            {titleCase(title)}
          </Badge>
        ) : null}

        <Field label={t("hist.file")} htmlFor="attachment-file" required>
          <label className="border-line-strong text-fg-muted text-subhead hover:border-accent hover:bg-accent-soft/40 rounded-card flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed p-4 transition-colors">
            {/* The document itself once there is one, rather than its name.
                A scan is picked out of a folder of scans, and "IMG_0413.jpg"
                is not how anybody tells one ID card from another — legacy
                shows the picture in the card for the same reason.

                Contained, not cropped: filling the box would cut the top and
                bottom off a card held landscape, which is where the number and
                the birthday are printed, and checking those is the whole point
                of looking at it before saving. */}
            {file && isImageData(file.url) ? (
              <img
                src={file.url}
                alt={file.name}
                className="max-h-52 w-auto max-w-full rounded-lg object-contain"
              />
            ) : file ? (
              <FileText className="text-fg-subtle size-8" aria-hidden />
            ) : null}
            <span className={cn(file && "text-fg-subtle text-footnote")}>
              {file ? file.name : t("rp.dropFile")}
            </span>
            {file ? (
              <span className="text-fg-subtle text-footnote">{t("att.clickToChange")}</span>
            ) : null}
            <input
              id="attachment-file"
              type="file"
              className="sr-only"
              accept={ATTACHMENT_ACCEPT}
              onChange={(event) => {
                const picked = event.target.files?.[0];
                event.target.value = "";
                if (!picked) return;
                if (picked.size > MAX_MB * 1024 * 1024) {
                  setError(t("rp.fileTooLarge").replace("{mb}", String(MAX_MB)));
                  return;
                }
                setError(null);
                const reader = new FileReader();
                reader.onload = () =>
                  setFile({ url: String(reader.result ?? ""), name: picked.name });
                reader.readAsDataURL(picked);
              }}
            />
          </label>
        </Field>

        {error ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}
